import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import styles from './RechargePage.module.css';
import logo from '../assets/logo.png';

const COOLDOWN_MS = 15000;
const MAX_DAILY_ATTEMPTS = 20;
const SESSION_KEY_PREFIX = 'recharge_session_';
const PIX_DATA_KEY = 'recharge_pix_data';
const POLLING_INTERVAL = 5000;

export default function RechargePage({ onLogout }) {
  const [user, setUser] = useState({});
  const [userId, setUserId] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [pixData, setPixData] = useState(null);
  // Estado para o checkbox de reativação de créditos expirados
  const [reativarExpirados, setReativarExpirados] = useState(false);
  const [responseInfo, setResponseInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const API_URL = useMemo(() => process.env.REACT_APP_API_URL || '', []);
  const abortControllerRef = useRef(null);
  const pollingRef = useRef(null);

  // ─── Helpers ─────────────────────────────────────────────────

  const getSessionKey = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    return `${SESSION_KEY_PREFIX}${userId || 'guest'}_${today}`;
  }, [userId]);

  const getSessionData = useCallback(() => {
    try {
      const raw = localStorage.getItem(getSessionKey());
      return raw ? JSON.parse(raw) : { attempts: 0, lastAttempt: 0, pendingId: null };
    } catch {
      return { attempts: 0, lastAttempt: 0, pendingId: null };
    }
  }, [getSessionKey]);

  const setSessionData = useCallback((data) => {
    try {
      localStorage.setItem(getSessionKey(), JSON.stringify(data));
    } catch {
      // localStorage cheio
    }
  }, [getSessionKey]);

  // ─── Persistência do Pix ─────────────────────────────────────

  const savePixData = useCallback((data, info) => {
    try {
      localStorage.setItem(PIX_DATA_KEY, JSON.stringify({ data, info, savedAt: Date.now() }));
    } catch {
      // ignore
    }
  }, []);

  const loadPixData = useCallback(() => {
    try {
      const raw = localStorage.getItem(PIX_DATA_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.savedAt > 3600000) {
        localStorage.removeItem(PIX_DATA_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, []);

  const clearPixData = useCallback(() => {
    localStorage.removeItem(PIX_DATA_KEY);
  }, []);

  // ─── Carregar pacotes ────────────────────────────────────────

  useEffect(() => {
    async function fetchPackages() {
      try {
        const res = await fetch(`${API_URL}/api/packages`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.packages || [];
        const valid = list.filter((p) => (
          typeof p?.amount === 'number' && p.amount > 0 &&
          typeof p?.credits === 'number' && p.credits > 0 &&
          typeof p?.label === 'string'
        ));
        setPackages(valid);
      } catch (err) {
        console.error('Erro ao carregar pacotes:', err);
        setError('Não foi possível carregar os pacotes.');
      } finally {
        setLoadingPackages(false);
      }
    }
    fetchPackages();
  }, [API_URL]);

  // ─── Auto-fill user + restaurar Pix persistido ─────────────

  useEffect(() => {
    const token = localStorage.getItem('motocheckToken');
    if (!token) { window.location.href = '/'; return; }

    async function fetchMe() {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data?.sucesso && data.user) {
          const u = data.user;
          setUserId(String(u.id ?? u.userId ?? ''));
          setUser(u);
          setEmpresa(u.empresa ?? u.name ?? u.empresaNome ?? '');

          const saved = loadPixData();
          if (saved) {
            setPixData(saved.data);
            setResponseInfo(saved.info);
            setHasPending(true);
          } else {
            const session = getSessionData();
            if (session.pendingId) {
              setSessionData({ attempts: session.attempts, lastAttempt: session.lastAttempt, pendingId: null });
              setHasPending(false);
            }
          }
        } else {
          window.location.href = '/';
        }
      } catch {
        window.location.href = '/';
      }
    }
    fetchMe();
  }, [API_URL, getSessionData, setSessionData, loadPixData]);

  // ─── POLLING: Verificar status do pagamento ─────────────────

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const checkPaymentStatus = useCallback(async () => {
    if (!pixData?.metadata?.rechargeId) return;

    const rechargeId = pixData.metadata.rechargeId;
    const token = localStorage.getItem('motocheckToken');

    try {
      setCheckingPayment(true);
      const res = await fetch(`${API_URL}/api/payments/status/${rechargeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();

      if (!json.sucesso) {
        console.error('Erro ao verificar status:', json.erro);
        return;
      }

      if (json.status === 'PAGO') {
        // 🎉 PAGAMENTO CONFIRMADO!
        stopPolling();
        setHasPending(false);
        setPixData(null);
        setResponseInfo(null);
        clearPixData();
        setError(null);

        // Atualiza créditos do usuário no estado
        setUser(prev => ({
          ...prev,
          credits: (prev.credits || 0) + (json.amount ? Math.round(json.amount) * 100 : 0)
        }));

        setPaymentSuccess({
          amount: json.amount,
          paidAt: json.paidAt,
        });

        // Limpa mensagem de sucesso depois de 10s
        setTimeout(() => setPaymentSuccess(null), 10000);

        // Atualiza session
        const session = getSessionData();
        setSessionData({ attempts: session.attempts, lastAttempt: session.lastAttempt, pendingId: null });

      } else if (json.status === 'NAO_PAGO') {
        // Expirou ou não existe mais
        stopPolling();
        setHasPending(false);
        setPixData(null);
        setResponseInfo(null);
        clearPixData();
        setError('Cobrança expirada. Gere uma nova.');

        const session = getSessionData();
        setSessionData({ attempts: session.attempts, lastAttempt: session.lastAttempt, pendingId: null });
      }
      // Se PENDENTE, continua o polling

    } catch (err) {
      console.error('Erro no polling:', err);
    } finally {
      setCheckingPayment(false);
    }
  }, [pixData, API_URL, stopPolling, clearPixData, getSessionData, setSessionData]);

  useEffect(() => {
    if (!hasPending || !pixData?.metadata?.rechargeId) {
      stopPolling();
      return;
    }

    const startPolling = () => {
      checkPaymentStatus();
      pollingRef.current = setTimeout(startPolling, POLLING_INTERVAL);
    };

    startPolling();

    return () => stopPolling();
  }, [hasPending, pixData, checkPaymentStatus, stopPolling]);

  // ─── Checkout ────────────────────────────────────────────────

  const handleCheckout = useCallback(async (pkg) => {
    if (loading || hasPending) return;

    const match = packages.find((p) => p.amount === pkg.amount && p.credits === pkg.credits);
    if (!match) {
      setError('Pacote inválido.');
      return;
    }

    const now = Date.now();
    const session = getSessionData();

    if (now - session.lastAttempt < COOLDOWN_MS) {
      const rem = Math.ceil((COOLDOWN_MS - (now - session.lastAttempt)) / 1000);
      setError(`Aguarde ${rem}s.`);
      return;
    }

    if (session.attempts >= MAX_DAILY_ATTEMPTS) {
      setError('Limite diário atingido.');
      return;
    }

    // Limpa sucesso anterior se houver
    setPaymentSuccess(null);

    const payload = {
      amount: match.amount,
      description: `${match.credits - (match.bonus || 0)} créditos${match.bonus ? ` + Bônus ${match.bonus}` : ''}.`,
      externalId: userId || 'guest',
      payerName: empresa || 'empresa',
      expiresIn: 3600,
      metadata: {
        rechargeId: `${userId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        origin: 'lead-checker',
        bonus: match.bonus || 0,
      },
    };

    // Adiciona campo para reativar créditos expirados se o checkbox estiver marcado
    if (reativarExpirados) {
      payload.reativarCreditosExpirados = true;
    }

    const token = localStorage.getItem('motocheckToken');

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      setError(null);
      setPixData(null);
      setCopied(false);
      setLoading(true);
      setHasPending(true);

      setSessionData({
        attempts: session.attempts + 1,
        lastAttempt: Date.now(),
        pendingId: payload.metadata.rechargeId,
      });

      const res = await fetch(`${API_URL}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
      });

      const json = await res.json();

      if (!res.ok) {
        setHasPending(false);
        setSessionData({ ...getSessionData(), pendingId: null });
        setError(`Erro: ${json?.erro || json?.message || res.status}`);
        return;
      }

      const receivedData = json?.data ?? null;
      setResponseInfo(json);
      setPixData(receivedData);
      savePixData(receivedData, json);

    } catch (e) {
      setHasPending(false);
      setSessionData({ ...getSessionData(), pendingId: null });
      setError(e.name === 'AbortError' ? 'Cancelado.' : 'Falha de conexão.');
    } finally {
      setLoading(false);
    }
  }, [loading, hasPending, packages, userId, empresa, API_URL, getSessionData, setSessionData, savePixData, reativarExpirados]);

  // ─── Cancelar / Nova cobrança ──────────────────────────────

  const clearPending = useCallback(() => {
    stopPolling();
    setHasPending(false);
    setPixData(null);
    setResponseInfo(null);
    setPaymentSuccess(null);
    clearPixData();
    const session = getSessionData();
    setSessionData({ attempts: session.attempts, lastAttempt: session.lastAttempt, pendingId: null });
  }, [getSessionData, setSessionData, clearPixData, stopPolling]);

  // ─── Copiar Pix ──────────────────────────────────────────────

  async function copyPixCode() {
    if (!pixData?.pixQrCode) return;
    try {
      await navigator.clipboard.writeText(pixData.pixQrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Erro ao copiar.');
    }
  }

  // ─── Cleanup ─────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      stopPolling();
    };
  }, [stopPolling]);

  // ─── Render ──────────────────────────────────────────────────

  if (loadingPackages) {
    return (
      <div className="appLoading">
        <div className="appLoadingCard"><span>Carregando valores...</span></div>
      </div>
    );
  }

  const isLocked = loading || hasPending;

  return (
    <div className="history-root">
      {/* Header */}

      {/* <header className={styles.header}>

        <div className={styles.logo}>
          <img
            src={logo}
            alt="Analise Central"
            className={styles.logoImage}
          />
        </div>
        <div className={styles.accountBox}>
          <div className={styles.accountMeta}>
            <strong>{empresa}</strong>
            <span className={styles.creditInfo}>{user?.credits || '-'} créditos</span>
          </div>
          <a href="/" className={styles.btnRecargas}>Início</a>
          <button className={styles.btnLogout} onClick={onLogout} type="button">Sair</button>
        </div>
      </header> */}

      <header className="history-header">
        <div className="container">
          <div className="history-header-inner">
            <a href="/" className="history-logo">Análise Central</a>
            <nav className="history-header-nav">
              <a href="/">Inicio</a>
              <a href="/historico" >Histórico</a>
              <a href="#" className="active">Recargas</a>
            </nav>
            <div className="history-user">
              <span className="user-name">{user?.empresa || 'Conta autenticada'}</span>
              <button className="btn-sair" onClick={onLogout}>Sair</button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="history-hero">
        <div className="container">
          <span className="history-hero-label">Área do Cliente</span>
          <h1>Recarregue seus <span className={styles.heroAccent}>créditos</span></h1>
          <p className="history-hero-desc">
            Escolha um pacote e pague via Pix de forma rápida e segura.
          </p>
        </div>
      </div>

      <main className={styles.main}>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <span className={styles.cardLabel}>Recargas</span>
              <h2 className={styles.cardTitle}>Escolha seu pacote</h2>
            </div>
            <span className={styles.cardHint}>Pagamento via Pix</span>
          </div>

          {/* ✅ SUCESSO DO PAGAMENTO */}
          {paymentSuccess && (
            <div className={styles.successBox}>
              <div className={styles.successIcon}>🎉</div>
              <div className={styles.successContent}>
                <strong>Pagamento confirmado!</strong>
                <p>Seus créditos foram adicionados com sucesso.</p>
              </div>
            </div>
          )}

          {/* ⏳ AGUARDANDO PAGAMENTO */}
          {hasPending && pixData && !paymentSuccess && (
            <div className={styles.awaitingBox}>
              <div className={styles.spinnerSmall} />
              <div>
                <strong>Aguardando pagamento...</strong>
              </div>
              <button onClick={clearPending} className={styles.linkButton} type="button">Cancelar</button>
            </div>
          )}


          {pixData && (
            <div className={styles.pixSection}>
              <div className={styles.pixCard}>
                <div className={styles.pixHeader}>
                  <h3 className={styles.pixTitle}>Cobrança Pix gerada</h3>
                  <p className={styles.pixSubtitle}>Escaneie o QR Code ou copie o código abaixo</p>
                </div>

                <div className={styles.pixDetails}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Nome</span>
                    <span className={styles.detailValue}>{responseInfo?.data?.payerName ?? pixData?.payerName ?? '-'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Valor</span>
                    <span className={styles.detailValue}>R$ {responseInfo?.data?.amount ?? pixData?.amount ?? '-'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Descrição</span>
                    <span className={styles.detailValue}>{responseInfo?.data?.description ?? pixData?.description ?? '-'}</span>
                  </div>
                </div>

                <div className={styles.qrWrapper}>
                  <img src={pixData.qrCodeImage} alt="QR Code Pix" className={styles.qrCode} />
                </div>

                <div className={styles.pixCodeBox}>
                  <span className={styles.pixCodeLabel}>Código Pix (copia e cola)</span>
                  <textarea readOnly value={pixData.pixQrCode} className={styles.pixCode} onClick={(e) => e.target.select()} />
                  <button onClick={copyPixCode} className={`${styles.copyButton} ${copied ? styles.copied : ''}`} type="button">
                    {copied ? '✅ Copiado!' : 'Copiar código Pix'}
                  </button>
                </div>

                <div className={styles.pixActions}>
                  <button onClick={clearPending} className={styles.secondaryButton} type="button">Gerar nova cobrança</button>
                </div>
              </div>
            </div>
          )}

          <div className={styles.grid}>
            {packages.map((pkg, idx) => (
              <div
                key={idx}
                className={`${styles.cardItem} ${pkg.best ? styles.best : ''} ${isLocked ? styles.disabled : ''}`}
                onClick={() => !isLocked && handleCheckout(pkg)}
                role="button"
                tabIndex={isLocked ? -1 : 0}
                onKeyDown={(e) => e.key === 'Enter' && !isLocked && handleCheckout(pkg)}
                aria-disabled={isLocked}
              >
                <div className={styles.cardTop}>
                  <strong className={styles.price}>{pkg.label}</strong>
                  <button
                    onClick={(e) => { e.stopPropagation(); !isLocked && handleCheckout(pkg); }}
                    className={styles.payButton}
                    disabled={isLocked}
                    type="button"
                  >
                    {loading && !pixData ? <span className={styles.spinnerText}>Gerando...</span>
                      : hasPending ? <span>Pendente</span>
                        : <>Pix</>}
                  </button>
                </div>
                <div className={styles.cardBody}>
                  <span className={styles.credits}>{(Number(pkg.credits)-Number(pkg.bonus)).toLocaleString()} créditos</span>
                  {pkg.bonus > 0 && <div className={styles.bonus}>{pkg.bonus.toLocaleString()} bônus</div>}
                </div>
                {pkg.best && <span className={styles.bestTag}>Melhor Custo</span>}
              </div>
            ))}
          </div>

          {/* Checkbox para reativar créditos expirados */}
          {user.creditsExpired && <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: "5px 22px"  }}>
            <input
              type="checkbox"
              id="reativar-expirados"
              checked={reativarExpirados}
              onChange={e => setReativarExpirados(e.target.checked)}
              disabled={isLocked}
              style={{ width: 20, height: 20 }}
            />
            <label htmlFor="reativar-expirados" style={{ fontSize: "1.1rem", fontWeight: 800, cursor: isLocked ? 'not-allowed' : 'pointer' }}>
              Reativar créditos expirados ( + R$ 10,00 - limitado a 5 mil creditos )
            </label>
          </div>}
          {loading && !pixData && (
            <div className={styles.loadingBox}>
              <div className={styles.spinner} />
              <span className={styles.loadingText}>Gerando cobrança Pix...</span>
            </div>
          )}

          {error && (
            <div className={styles.errorBox}>
              {error}
              <button onClick={() => setError(null)} className={styles.closeError} type="button" aria-label="Fechar">✕</button>
            </div>
          )}

          <div className={styles.backRow}>
            <button onClick={() => window.history.back()} className={styles.backButton} type="button">← Voltar</button>
          </div>
        </div>
      </main>
    </div>
  );
}